<!--
  ~ Copyright (C) 2025 - Potentially Ltd
  ~
  ~ Please see distribution for license.
  -->

<div class="control-panel-container">
  <div class="control-panel__first-row">
    <mat-menu #manageReviewersMenu>
      <button
        mat-menu-item
        [attr.aria-label]="'translations.global.ariaLabels.tapToCloseSubmissions' | transloco"
        [class.is_disabled]="!checkedMembers.length"
        (click)="reviewAction(reviewActions.CLOSE)"
      >
        <span class="body-s">{{ 'translations.reviews.submissionsControls.buttons.closeSelectedReviews' | transloco }}</span>
      </button>
      <mat-divider class="export-divider"></mat-divider>
      <button
        mat-menu-item
        [attr.aria-label]="'translations.global.ariaLabels.tapToReOpenSubmissions' | transloco"
        [class.is_disabled]="!checkedMembers.length"
        (click)="reviewAction(reviewActions.REOPEN)"
      >
        <span class="body-s">{{ 'translations.reviews.submissionsControls.buttons.reopenSelectedReviews' | transloco }}</span>
      </button>
    </mat-menu>

    <mat-menu #attendanceMenu class="attendance-menu">
      <button
        mat-menu-item
        class="attendance-menu-item"
        [attr.aria-label]="'translations.global.ariaLabels.willNotAttend' | transloco"
        (click)="invokeEventAction('WILL_NOT_ATTEND')"
      >
        <span class="body-s">{{ 'translations.membersNew.actionBar.willNotAttend' | transloco }}</span>
      </button>
      <mat-divider class="export-divider"></mat-divider>
      <button
        mat-menu-item
        class="attendance-menu-item"
        [attr.aria-label]="'translations.global.ariaLabels.willAttend' | transloco"
        (click)="invokeEventAction('WILL_ATTEND')"
      >
        <span class="body-s">{{ 'translations.membersNew.actionBar.willAttend' | transloco }}</span>
      </button>
      <mat-divider class="export-divider"></mat-divider>
      @if (isWaitingListEnabled) {
        <button
          mat-menu-item
          class="attendance-menu-item"
          [class.is_disabled]="isWaitingListOperationDisabled"
          [disabled]="isWaitingListOperationDisabled"
          [attr.aria-label]="'translations.global.ariaLabels.waitingList' | transloco"
          (click)="invokeEventAction('WAITING_LIST')"
        >
          <span class="body-s">{{ 'translations.membersNew.actionBar.waitingList' | transloco }}</span>
        </button>
        <mat-divider class="export-divider"></mat-divider>
      }
      <button
        mat-menu-item
        class="attendance-menu-item"
        [attr.aria-label]="'translations.global.ariaLabels.haveAttended' | transloco"
        (click)="invokeEventAction('HAVE_ATTENDED')"
      >
        <span class="body-s">{{ 'translations.membersNew.actionBar.haveAttended' | transloco }}</span>
      </button>
    </mat-menu>
  </div>

  <div class="control-panel-action-buttons">
    @if (isPlaylistPage && canManageReviews) {
      <ptl-button
        [size]="'s'"
        [type]="'soft-neutral'"
        [icon]="LucideClipboardCheck"
        [text]="'translations.membersNew.actionBar.manageReviewStatus' | transloco"
        [disabled]="!checkedMembers.length"
        [matMenuTriggerFor]="checkedMembers.length ? manageReviewersMenu : null"
        [matTooltip]="checkedMembers.length ? '' : ('translations.membersNew.actionBar.manageReviewStatus' | transloco)"
        matTooltipClass="medium-tooltip"
        [attr.aria-label]="'translations.global.ariaLabels.manageReviewStatus' | transloco"
      ></ptl-button>
    }

    @if (isAdminPage) {
      <ptl-button
        [size]="'s'"
        [type]="'soft-neutral'"
        [icon]="LucideShield"
        [text]="'translations.membersNew.actionBar.editRole' | transloco"
        [disabled]="!checkedMembers.length"
        [matTooltip]="checkedMembers.length > 0 ? '' : ('translations.membersNew.actionBar.noMemberSelect' | transloco)"
        matTooltipClass="medium-tooltip"
        (clicked)="openEditRoleDialog()"
        [attr.aria-label]="'translations.global.ariaLabels.editRole' | transloco"
      ></ptl-button>
    }

    @if (!isIframeMode) {
      <ptl-button
        [size]="'s'"
        [type]="'soft-neutral'"
        [icon]="LucideUsers"
        [text]="'translations.membersNew.actionBar.editGroup' | transloco"
        [disabled]="!checkedMembers.length"
        [matTooltip]="checkedMembers.length > 0 ? '' : ('translations.membersNew.actionBar.noMemberSelect' | transloco)"
        matTooltipClass="medium-tooltip"
        (clicked)="openEditGroupDialog()"
        [attr.aria-label]="'translations.global.ariaLabels.editGroup' | transloco"
      ></ptl-button>
    }

    <ptl-button
      [size]="'s'"
      [type]="'soft-neutral'"
      [icon]="LucideMail"
      [text]="'translations.membersNew.controlPanel.message' | transloco"
      (clicked)="openSendMessageDialog()"
      [attr.aria-label]="'translations.global.ariaLabels.sendMessage' | transloco"
    ></ptl-button>

    @if (isEvent) {
      <ptl-button
        [size]="'s'"
        [type]="'soft-neutral'"
        [icon]="LucideQrCode"
        [text]="'translations.qrcode.checkIn' | transloco"
        [disabled]="!cardPublished"
        [matTooltip]="!cardPublished ? ('translations.qrcode.eventMustBePublished' | transloco) : ''"
        (clicked)="openCheckInDialog()"
      ></ptl-button>
    }

    @if (!isCardPage && !isPlaylistPage && !isEvent && (canManageUserImportPrivileges$ | async) === true) {
      <ptl-button
        [size]="'s'"
        [type]="'soft-neutral'"
        [icon]="LucideUpload"
        [text]="'translations.membersNew.controlPanel.usersUpload.name' | transloco"
        (clicked)="openUsersUploadCsv()"
        [attr.aria-label]="'translations.global.ariaLabels.usersUpload' | transloco"
      ></ptl-button>
    }

    @if (!isCardPage && !isPlaylistPage && (organizationData$ | async)?.type !== 'FOLIO') {
      <ptl-button
        [size]="'s'"
        [type]="'soft-neutral'"
        [icon]="LucideUpload"
        [text]="'translations.membersNew.controlPanel.alumniUpload.name' | transloco"
        (clicked)="openAlumniUploadCsv()"
        [attr.aria-label]="'translations.global.ariaLabels.userAlumniUpload' | transloco"
      ></ptl-button>
    }

    @if (isEvent) {
      <ptl-button
        [size]="'s'"
        [type]="'soft-neutral'"
        [icon]="LucideCalendarCheck"
        [text]="'translations.membersNew.actionBar.attendance' | transloco"
        [disabled]="!checkedMembers.length"
        [matMenuTriggerFor]="checkedMembers.length >= 1 ? attendanceMenu : null"
        [matTooltip]="checkedMembers.length > 0 ? '' : ('translations.membersNew.actionBar.noMemberSelect' | transloco)"
        matTooltipClass="medium-tooltip"
        [attr.aria-label]="'translations.global.ariaLabels.attendance' | transloco"
      ></ptl-button>
    }
  </div>
</div>
